// Arm parts (separate runtime sprites, never baked into the body sheet).
// Each part is authored pointing RIGHT from its pivot: the upper arm from the
// shoulder pivot (0,0) to the elbow (upperLen, 0); the forearm from the elbow
// (0,0) to the grip point (foreLen, 0), where the closed fist wraps the
// weapon grip. The shoulder pauldron rides on the upper arm so it rotates with
// the aim. +y is the underside of the arm, the key light comes from the
// texture's upper-left (−x, −y).

import type { MechId } from '../../core/types';
import type { Drawer } from './draw';
import type { Bounds } from './sprite';
import type { MechStyle } from './styles';

export interface ArmLib {
  upper(d: Drawer, len: number): void;
  fore(d: Drawer, len: number): void;
  upperBounds: Bounds;
  foreBounds: Bounds;
}

// ───────────────────────────── VOLT ─────────────────────────────
// Slim racing arm, swept angular pauldron with cobalt trim, gauntlet light strip.

const VOLT_ARM: ArmLib = {
  upperBounds: { x0: -8.5, y0: -9, x1: 21, y1: 5.5 },
  foreBounds: { x0: -4.5, y0: -6, x1: 25, y1: 6 },
  upper(d, L) {
    d.plate([[-2, -2.8], [L, -2.4], [L, 2.4], [-2, 2.8]], 'frame', { sh: 1.2 });
    d.plate([[3, -4], [L - 4, -3.7], [L - 1.6, -1], [L - 3.2, 3.4], [4, 4], [2, 0]], 'armor', { sh: 2.4, hl: 1 });
    d.panel([[L - 8, -3.8], [L - 8.6, 4]]);
    d.plate([[-7, -6.4], [3, -8], [11, -5.6], [12.6, -1.6], [9, 2.6], [-2, 4.6], [-7.6, 1]], 'armor', { sh: 3, hl: 1.2, r: 0.7 });
    d.edge([[-6.8, -6.1], [3, -7.7], [10.8, -5.4]]);
    d.plate([[-4.5, -5.6], [3.2, -7], [9.6, -5], [2, -4]], 'accent', { sh: 0.8, hl: 0.6, ink: 0.6 });
    d.panel([[0.5, -2.5], [10, -1.5]]);
    d.bolt(-3.6, -0.4, 0.7);
  },
  fore(d, L) {
    d.disc(0, 0, 3.4, 'metal');
    d.plate([[0, -2.4], [L - 5, -2.4], [L - 5, 2.4], [0, 2.4]], 'frame', { sh: 1 });
    d.plate([[1.6, -3.8], [L - 8, -4.8], [L - 4.4, -3.2], [L - 4.4, 3], [L - 9, 4.4], [2.2, 3.4]], 'armor', { sh: 2.4, hl: 1, r: 0.6 });
    d.edge([[1.8, -3.6], [L - 8, -4.6]]);
    d.plate([[L - 10, -4.5], [L - 4.4, -3.2], [L - 4.4, -0.6], [L - 10, -1.6]], 'accent', { sh: 0.7, hl: 0.6, ink: 0.6 });
    d.strip([[3.6, -2.4], [L - 11.5, -2.9]], 1, 0.95);
    // Closed fist around the grip (grip point = (L, 0)).
    d.plate([[L - 5, -3], [L + 0.6, -3.4], [L + 3, -1.6], [L + 3, 3.4], [L, 4.8], [L - 5, 3.8]], 'plate', { sh: 1.4, hl: 0.8 });
    d.panel([[L - 1.6, -3], [L - 1.6, 4.4]], 0.6);
    d.panel([[L + 1, -2], [L + 1, 4]], 0.6);
  },
};

// ───────────────────────────── BASTION ─────────────────────────────
// Heavy arm: slab pauldron with a hazard band, armoured gauntlet with vents.

const BASTION_ARM: ArmLib = {
  upperBounds: { x0: -11.5, y0: -11.5, x1: 20.5, y1: 7 },
  foreBounds: { x0: -5.5, y0: -7.5, x1: 26.5, y1: 7.5 },
  upper(d, L) {
    d.plate([[-2, -3.6], [L, -3.2], [L, 3.2], [-2, 3.6]], 'frame', { sh: 1.4 });
    d.plate([[2, -5.4], [L - 4, -5], [L - 1.2, -1.4], [L - 3, 5], [3, 5.4], [1, 0]], 'armor', { sh: 3, hl: 1.1, r: 0.8 });
    d.panel([[L - 8, -5], [L - 8, 5.2]]);
    d.bolt(L - 5.5, 2.4);
    d.plate([[-10, -9], [8, -11], [14, -8], [15.2, -1], [11, 4], [-4, 6.2], [-11, 2]], 'armor', { sh: 4, hl: 1.3, r: 0.9 });
    d.edge([[-9.8, -8.7], [8, -10.7], [13.8, -7.8]]);
    d.plate([[-8, -8.2], [7.6, -10], [12.6, -7.4], [-3, -5.8]], 'accent', { sh: 0.8, hl: 0.7, ink: 0.6 });
    d.panel([[-9, -1], [12, -2]]);
    d.bolt(-7.5, 1);
    d.bolt(9.5, 0.4);
  },
  fore(d, L) {
    d.disc(0, 0, 4.6, 'metal');
    d.plate([[0, -3.2], [L - 4, -3.2], [L - 4, 3.2], [0, 3.2]], 'frame', { sh: 1.2 });
    d.plate([[1, -5.4], [L - 7, -6.4], [L - 3, -4.4], [L - 3, 4.6], [L - 7, 6.4], [1, 5.4], [-1, 0]], 'armor', { sh: 3.4, hl: 1.2, r: 0.8 });
    d.edge([[1.2, -5.2], [L - 7, -6.2]]);
    d.panel([[L - 11, -6], [L - 11, 6]]);
    d.plate([[L - 10.5, -6], [L - 3, -4.4], [L - 3, -1.4], [L - 10.5, -2.4]], 'accent', { sh: 0.8, hl: 0.6, ink: 0.6 });
    d.strip([[3.5, 3.4], [L - 13, 3.8]], 1.1, 0.8);
    d.bolt(3.5, -2.4);
    d.plate([[L - 4, -4], [L + 2, -4.2], [L + 4.5, -2], [L + 4.5, 4.6], [L + 1, 6], [L - 4, 5]], 'plate', { sh: 1.6, hl: 0.8 });
    d.panel([[L - 0.5, -3.8], [L - 0.5, 5.6]], 0.6);
    d.panel([[L + 2.2, -2.8], [L + 2.2, 5]], 0.6);
  },
};

// ───────────────────────────── WRAITH ─────────────────────────────
// Long slender arm, small shoulder fin, blade-like forearm plate with a violet strip.

const WRAITH_ARM: ArmLib = {
  upperBounds: { x0: -6.5, y0: -7.5, x1: 21.5, y1: 4.5 },
  foreBounds: { x0: -10, y0: -7, x1: 25, y1: 5 },
  upper(d, L) {
    d.plate([[-1.5, -2.2], [L, -1.9], [L, 1.9], [-1.5, 2.2]], 'frame', { sh: 1 });
    d.plate([[2, -3.2], [L - 4, -2.8], [L - 2, -0.5], [L - 4, 2.6], [3, 3], [1, 0]], 'armor', { sh: 2, hl: 0.9, r: 0.5 });
    d.panel([[L - 9, -2.9], [L - 9.4, 3]]);
    d.plate([[-5, -4], [2, -6.4], [11, -4.6], [6, -1], [-3, 2]], 'armor', { sh: 2.2, hl: 1, r: 0.5 });
    d.edge([[-4.8, -3.8], [2, -6.1], [10.6, -4.5]]);
    d.plate([[-2, -4.4], [2.4, -5.6], [8, -4.4], [3, -3]], 'accent', { sh: 0.6, hl: 0.5, ink: 0.5 });
  },
  fore(d, L) {
    // Blade fin sweeping back past the elbow.
    d.plate([[-9, -5.6], [6, -4.6], [14, -4.4], [4, -2.6], [-6, -3]], 'accent', { sh: 1, hl: 0.7, r: 0.4 });
    d.disc(0, 0, 2.8, 'metal');
    d.plate([[0, -1.9], [L - 5, -1.9], [L - 5, 1.9], [0, 1.9]], 'frame', { sh: 0.8 });
    d.plate([[1, -3], [L - 6, -3.5], [L - 3.5, -1.5], [L - 5, 2.5], [2, 2.5]], 'armor', { sh: 2, hl: 0.9, r: 0.5 });
    d.edge([[1.2, -2.8], [L - 6, -3.3]]);
    d.strip([[4, -0.9], [L - 7, -1.5]], 1, 1);
    d.plate([[L - 5, -2.5], [L, -2.8], [L + 2, -1], [L + 2, 3], [L - 0.5, 4], [L - 5, 3.2]], 'plate', { sh: 1.2, hl: 0.7 });
    d.panel([[L - 1.6, -2.4], [L - 1.6, 3.6]], 0.55);
  },
};

export const ARM_LIBS: Readonly<Record<MechId, ArmLib>> = { volt: VOLT_ARM, bastion: BASTION_ARM, wraith: WRAITH_ARM };

export const armLengths = (s: MechStyle): { upper: number; fore: number } => ({ upper: s.arm.upperLen, fore: s.arm.foreLen });
